import { MobXProviderContext } from "mobx-react";
import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  publicHeroCardStyle,
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
} from "../../../components/general/publicPageStyles";
import { redirectToHomeForAuthMode } from "../../../utils/stringUtils";
import { getHoursSavedRows } from "./WhyAfhHub";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const Root = () => {
  const { authStore } = useContext(MobXProviderContext);
  const mobile = useResponsive().isMobile;
  const authExperience = useAuthExperience();
  const hoursSavedRows = getHoursSavedRows(authExperience.brandKey, authExperience.discovery.jobWebsiteLabel);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const resolveSignedInHome = async () => {
      try {
        const authenticated = await authStore.isUserLoggedIn();
        if (!isMounted || !authenticated) {
          return;
        }

        const roleType = authStore.getUserRoleType();
        setRedirectPath(
          roleType
            ? redirectToHomeForAuthMode(authStore.getAuthMode(), roleType)
            : RouterAddress.PROVIDER_HOME,
        );
      } catch {
        if (isMounted) {
          setRedirectPath(null);
        }
      } finally {
        if (isMounted) {
          setAuthCheckComplete(true);
        }
      }
    };

    void resolveSignedInHome();

    return () => {
      isMounted = false;
    };
  }, [authStore]);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (!authCheckComplete) {
    return null;
  }

  return (
    <div style={publicPageContainerStyle}>
      <div style={{ ...publicHeroCardStyle, padding: mobile ? "1rem" : publicHeroCardStyle.padding }}>
        <div style={{ margin: "0 auto 1rem", textAlign: "center", maxWidth: "100%" }}>
          <h1 style={{ color: "#0f3d63", fontSize: mobile ? "1.35rem" : "clamp(1.5rem, 2.3vw, 1.95rem)", lineHeight: 1.15, margin: 0 }}>
            {authExperience.publicSite.heroTitle}
          </h1>
          <p style={{ color: "#185da2", fontSize: mobile ? "0.9rem" : "0.95rem", fontWeight: 700, margin: "0.45rem 0 0" }}>
            {authExperience.publicSite.heroSubtitle}
          </p>
        </div>
        <div className="grid align-items-start">
          <div className="col-12 lg:col-6">
            <div className="mb-3 flex flex-wrap justify-content-center align-items-center gap-2" style={{ textAlign: "center" }}>
              <div
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl"
                style={{ background: "#f97316", border: "1px solid #ea580c", color: "#ffffff", fontWeight: 900, fontSize: "0.82rem" }}
              >
                <i className="pi pi-star-fill" />
                Free Start
              </div>
              <div
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl"
                style={{ background: "#f59e0b", border: "1px solid #d97706", color: "#ffffff", fontWeight: 900, fontSize: "0.82rem", letterSpacing: "0.03em" }}
              >
                <i className="pi pi-clock" />
                7 DAYS INCLUDED
              </div>
            </div>
            <div
              className="mb-4"
              style={{
                borderRadius: "18px",
                border: "1px solid #d9e8f5",
                background: "#ffffff",
                padding: "0.95rem 1rem",
              }}
            >
              <div className="flex align-items-center mb-2">
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1.08rem" }}>
                  Start with 1 resident
                </div>
              </div>
              <div style={{ color: "#5f7488", fontSize: "0.92rem", marginBottom: "0.55rem" }}>
                Try these care workflows with your first resident:
              </div>
              <div className="grid row-gap-2">
                {["Assessment → Care Plan", "Behavior Tracking", "Progress Notes", "eMAR", "ADL Tracking", "Incident Reporting", "Body Chart", "Nurse Assessment", "Delegation Forms"].map((feature) => (
                  <div key={feature} className="col-12 sm:col-6 md:col-4 py-1">
                    <div className="flex align-items-center gap-2 h-full" style={{ color: "#0f3d63", fontWeight: 650, fontSize: "0.9rem" }}>
                      <i className="pi pi-check-circle" style={{ color: "#16a34a", flex: "0 0 auto" }} />
                      <span>{feature}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-content-center">
                <div
                  className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl"
                  style={{ background: "#ecfdf5", border: "1px solid #86efac", color: "#166534", fontWeight: 800, fontSize: "0.82rem" }}
                >
                  <i className="pi pi-shield" />
                  DSHS-compliant forms across the workflow
                </div>
              </div>
            </div>

          </div>

          <div className="col-12 lg:col-6 flex flex-column align-items-center">
            <div className="mb-3" style={{ textAlign: "center" }}>
              <a
                href={RouterAddress.HIPAA_COMPLIANCE_PAGE}
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl"
                style={{
                  background: "#0f3d63",
                  color: "#ffffff",
                  fontWeight: 900,
                  fontSize: "0.82rem",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                }}
              >
                <i className="pi pi-shield" />
                HIPAA Compliant
              </a>
            </div>
            <div
              className="border-round-xl text-center"
              style={{
                backgroundColor: "rgba(255,255,255,0.82)",
                border: "1px solid #d9e8f5",
                width: "100%",
                margin: 0,
                padding: "0.95rem 1rem",
              }}
            >
              <div className="grid text-left">
                {[
                  { title: "Less manual entry", description: "Generate care plans, medication lists, and daily tasks from existing records." },
                  { title: "Binder sharing", description: "Share binders with authorized reviewers for faster feedback and review." },
                  { title: "Staff hiring workflow", description: "Ask hiring questions and collect certifications in an organized process." },
                  { title: "Staff management", description: "Keep staff access, roles, credentials, and home assignments together." },
                ].map((item, index, items) => (
                  <div key={item.title} className="col-12 p-0">
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "minmax(0, 0.9fr) minmax(0, 1.4fr)" : "minmax(150px, 0.85fr) minmax(0, 1.5fr)", alignItems: "start", gap: "0.65rem", borderBottom: index < items.length - 1 ? "1px solid #d9e8f5" : "none", padding: "0.7rem 0", background: "#ffffff" }}>
                      <div className="flex align-items-center gap-2" style={{ color: "#0f3d63", fontWeight: 650, fontSize: "0.9rem", lineHeight: 1.55 }}>
                        <i className="pi pi-check-circle" style={{ color: "#16a34a" }} />
                        <span>{item.title}</span>
                      </div>
                      <div style={{ color: "#5f7488", fontSize: "0.9rem", lineHeight: 1.55 }}>
                        {item.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-content-center align-items-center gap-3 mt-4">
          <a
            href={RouterAddress.PLATFORM_FEATURES_PAGE}
            rel="noopener noreferrer"
            className="px-4 py-3 border-round-lg"
            style={{ backgroundColor: "#0f3d63", color: "#ffffff", fontWeight: 700, textDecoration: "none", textAlign: "center" }}
          >
            View Platform Features
          </a>
          <a
            href={RouterAddress.PRICING_PAGE}
            rel="noopener noreferrer"
            className="px-4 py-3 border-round-lg"
            style={{ backgroundColor: "#ffffff", color: "#0f3d63", border: "1px solid #d7e6f4", fontWeight: 700, textDecoration: "none", textAlign: "center" }}
          >
            Explore Pricing
          </a>
        </div>
      </div>

      <div className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center" }}>
        <div
          className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
          style={{ background: "#fff7ed", border: "1px solid #f1dbc7", color: "#9a3412", fontWeight: 700 }}
        >
          <i className="pi pi-clock" />
          {authExperience.publicSite.hoursSavedBadge}
        </div>
        <h2 style={{ color: "#0f3d63", marginTop: 0, fontSize: mobile ? "1.5rem" : "1.9rem" }}>
          What once took hours of paperwork now happens in minutes
        </h2>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "760px" }}>
          {authExperience.publicSite.hoursSavedDescription}
        </p>

        {mobile ? (
          <div className="mt-4 flex flex-column gap-3" style={{ textAlign: "left" }}>
            {hoursSavedRows.map((row) => (
              <div
                key={row.workflow}
                style={{
                  border: "1px solid #dde9f5",
                  borderRadius: "18px",
                  padding: "1rem",
                  background: "#ffffff",
                }}
              >
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1rem" }}>{row.workflow}</div>
                <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.6rem" }}>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>Traditional Manual Time</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{row.manualTime}</div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>With {authExperience.brandName}</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{row.withAfhHub}</div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>Estimated Time Saved</div>
                    <div style={{ color: "#166534", fontWeight: 800 }}>{row.timeSaved}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4" style={{ overflowX: "auto", textAlign: "left" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
              <thead>
                <tr style={{ background: "#f8fbff" }}>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Workflow</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Traditional Manual Time</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>With {authExperience.brandName}</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Estimated Time Saved</th>
                </tr>
              </thead>
              <tbody>
                {hoursSavedRows.map((row) => (
                  <tr key={row.workflow}>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#0f172a", fontWeight: 700 }}>{row.workflow}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#475569" }}>{row.manualTime}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#185da2", fontWeight: 700 }}>{row.withAfhHub}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#166534", fontWeight: 800 }}>{row.timeSaved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Root;
